import type { ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { Link as RouteLink, useNavigate } from 'react-router-dom';
import type { SingleAlertRecord } from '~/api/generated-api';
import { useAppDispatch } from '~/app/store';
import SettingsIcon from '~/assets/icons/new/settings.svg?react';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { EDIT_SUBSCRIPTION_SEARCH_PARAM } from '~/common/constants/notifications.constants';
import { AlertCategory, AlertEvent } from '~/common/models/alert.model';
import { NotificationType } from '~/common/models/notification.model';
import {
  SETTINGS_NOTIFICATIONS_ACCOUNT,
  SETTINGS_NOTIFICATIONS_SAFETY_AND_SECURITY,
  SETTINGS_NOTIFICATIONS_VEHICLES,
} from '~/common/models/pages/settingsPages.model';
import {
  type BuildAssetUrlFn,
  buildAssetUrl as defaultBuildAssetUrl,
} from '~/common/utils/asset-url.utils';
import { EVENT_NOTIFICATION_MAPPING } from '~/routes/notifications/constants';
import { useNotificationSettingsFullPage } from '~/routes/settings/hooks/useNotificationSettingsFullPage';
import { buildNotificationDetailPath } from '~/routes/settings/utils/notificationDetailPath.utils';
import { setEditableSubscriptionId } from '~/store/alerts/alertsSlice';
export interface NotificationOption {
  text: string | JSX.Element;
  id?: string;
  icon?: ReactNode;
  onClick?: (e: React.MouseEvent<HTMLElement>, handleClose: () => void) => void;
}

/**
 * Get the alert event message with links to vehicle details.
 *
 * @param alert - The alert record
 * @param buildAssetUrl - Optional function to build asset URLs. If not provided,
 *                        falls back to legacy URL format. For org-based routing,
 *                        pass the function from `useBuildAssetUrl` hook.
 */
export const getAlertEventMessage = (
  alert: SingleAlertRecord,
  buildAssetUrl: BuildAssetUrlFn = defaultBuildAssetUrl
) => {
  const eventDetails = alert?.event || alert?.lastGroupEvent?.event;
  const searchKey = eventDetails?.event;

  if (!searchKey) {
    return (
      <Trans
        i18nKey="settings:notifications.messages.unknownEventType"
        values={{ eventType: undefined }}
      />
    );
  }

  // Ensure contentVariableMap is always defined and safely destructured
  const contentVars = eventDetails?.contentVariableMap || {};

  const createTransMessage = (
    i18nKey: string,
    linkHref: string,
    values: object
  ) => {
    return (
      <Trans
        i18nKey={i18nKey}
        components={{
          alink: (
            <RouteLink
              onClick={(e) => {
                e.stopPropagation();
              }}
              to={linkHref}
            />
          ),
        }}
        values={values}
        parent="span"
        data-heap-redact-text
      />
    );
  };

  switch (searchKey) {
    case AlertEvent.LOW_WASHER_FLUID:
      return createTransMessage(
        `settings:notifications.messages.${searchKey.toLowerCase()}`,
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          alert_object_alias:
            contentVars.alert_object_alias || translateText('common:unknown'),
        }
      );
    case AlertEvent.OIL_LIFE:
      return createTransMessage(
        'settings:notifications.messages.oilLife',
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          alert_object_alias:
            contentVars.alert_object_alias || translateText('common:unknown'),
          alert_object_alias_oil_life:
            contentVars.alert_object_alias_oil_life ||
            translateText('common:unknown'),
        }
      );
    default:
      return (
        <Trans
          i18nKey="settings:notifications.messages.unknownEventType"
          values={{ eventType: undefined }}
        />
      );
  }
};

/** List page of a notification: its section's page. */
const getSettingsListPath = (
  notificationType: NotificationType | undefined,
  category: string | undefined
): string => {
  switch (notificationType) {
    case NotificationType.ACCOUNT_ACTIVITY:
    case NotificationType.USER_ACTIVITY:
      return SETTINGS_NOTIFICATIONS_ACCOUNT;
    case NotificationType.VEHICLE:
      return SETTINGS_NOTIFICATIONS_VEHICLES;
    default:
      return category === AlertCategory.SAFETY
        ? SETTINGS_NOTIFICATIONS_SAFETY_AND_SECURITY
        : SETTINGS_NOTIFICATIONS_VEHICLES;
  }
};

/**
 * List page that opens the edit dialog of a subscription, used while the
 * full-page settings are off.
 */
const buildSettingsNotificationsPath = (
  path: string,
  subscriptionId: string | undefined
): string => {
  if (!subscriptionId) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${EDIT_SUBSCRIPTION_SEARCH_PARAM}=${encodeURIComponent(subscriptionId)}`;
};

/**
 * The Settings action of a notification in the feed. With the full-page
 * settings on it opens the notification's settings page, or its list page
 * when it has none; otherwise it opens the list page with the notification's
 * edit dialog.
 * @param alert - The notification in the feed
 * @returns The menu option
 */
export const useSettingsNotificationOption = (
  alert: SingleAlertRecord
): NotificationOption => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isFullPageEnabled = useNotificationSettingsFullPage();
  return {
    text: translateText('settings:notificationList.actionMenu.settings'),
    onClick: (e, handleClose) => {
      e.stopPropagation();
      const eventType =
        alert?.event?.event || alert?.lastGroupEvent?.event?.event;
      const category =
        alert?.event?.category || alert?.lastGroupEvent?.event?.category;
      const notificationType = eventType
        ? EVENT_NOTIFICATION_MAPPING[
            eventType as keyof typeof EVENT_NOTIFICATION_MAPPING
          ]
        : undefined;
      const listPath = getSettingsListPath(notificationType, category);
      if (isFullPageEnabled) {
        navigate(
          buildNotificationDetailPath({
            category,
            event: eventType,
            subscriptionId: alert?.subscriptionId,
          }) ?? listPath
        );
      } else {
        const subscriptionId = alert?.subscriptionId;
        dispatch(setEditableSubscriptionId(subscriptionId));
        navigate(buildSettingsNotificationsPath(listPath, subscriptionId));
      }
      handleClose();
    },
    icon: <SettingsIcon />,
    id: 'settings',
  };
};
